import { create } from 'zustand'
import { Logging, LoggingCode } from '../helpers/types/logging'
import {
	createLoggingCode as createLoggingCodeRequest,
	deleteLoggingCode as deleteLoggingCodeRequest,
	fetchCarrierLoggings,
	fetchAllLoggings,
	fetchLoggingCodes as fetchLoggingCodesRequest,
	CreateLoggingCodePayload,
	updateLoggingCode as updateLoggingCodeRequest,
	UpdateLoggingCodePayload
} from '../api/logging'

export interface LoggingState {
	loggings: Logging[]
	loggingCodes: LoggingCode[]
	addLogging: (log: Logging) => void
	fetchLoggings: (
		carrierId?: number,
		force?: boolean
	) => Promise<void>
	fetchCarrierLoggings: (carrierId: number, options?: { onlyErrors: boolean }) => Promise<Logging[]>
	fetchAllLoggings: (force?: boolean) => Promise<void>
	fetchLoggingCodes: (force?: boolean) => Promise<void>
	createLoggingCode: (
		payload: CreateLoggingCodePayload
	) => Promise<LoggingCode>
	updateLoggingCode: (
		code: string,
		payload: UpdateLoggingCodePayload
	) => Promise<LoggingCode>
	deleteLoggingCode: (code: string) => Promise<void>
}

export const loggingStore = create<LoggingState>((set, get) => ({
	loggings: [],
	loggingCodes: [],
	addLogging: (log) => {
		set((state) => ({
			loggings: [log, ...state.loggings]
		}))
	},
	fetchLoggings: async (carrierId?, force = false) => {
		if (carrierId != null) {
			if (!force && get().loggings.some((l) => l.carrierId === carrierId)) {
				return
			}
			const loggings = await fetchCarrierLoggings(carrierId)
			set({ loggings })
			return
		}
		return get().fetchAllLoggings(force)
	},
	fetchCarrierLoggings: async (carrierId: number, options = { onlyErrors: false }) => {
		const loggings = await fetchCarrierLoggings(carrierId, options)
		set({ loggings })
		return loggings
	},
	fetchAllLoggings: async (force = false) => {
		if (!force && get().loggings.length > 0) return
		const loggings = await fetchAllLoggings()
		set({ loggings })
	},
	fetchLoggingCodes: async (force = false) => {
		if (!force && get().loggingCodes.length > 0) return
		const loggingCodes = await fetchLoggingCodesRequest()
		set({ loggingCodes })
	},
	createLoggingCode: async (payload) => {
		const loggingCode = await createLoggingCodeRequest(payload)
		set((state) => ({
			loggingCodes: [loggingCode, ...state.loggingCodes]
		}))
		return loggingCode
	},
	updateLoggingCode: async (code, payload) => {
		const loggingCode = await updateLoggingCodeRequest(
			code,
			payload
		)
		set((state) => ({
			loggingCodes: state.loggingCodes.map((item) =>
				item.code === code ? loggingCode : item
			)
		}))
		return loggingCode
	},
	deleteLoggingCode: async (code) => {
		await deleteLoggingCodeRequest(code)
		set((state) => ({
			loggingCodes: state.loggingCodes.filter(
				(item) => item.code !== code
			)
		}))
	}
}))
